
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __appWithI18n from 'next-translate/appWithI18n'
    import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import { AppProps } from 'next/app';
import Router, { useRouter } from 'next/router';
import Script from 'next/script';
import NProgress from 'nprogress'; //nprogress module
import { useEffect } from 'react';
import { Toaster } from 'sonner';
// import 'nprogress/nprogress.css'; //styles of nprogress
// This is for demo purposes, remove @/styles/colors.css import immediately
import '@/styles/globals.css';
import { GTM_ID } from '@/lib/gtm';
import AppErrorBoundary from '@/components/AppErrorBoundary';
import CookieConsent from '@/components/CookieConsent';
import LocaleInitializer from '@/components/layout/LocaleInitializer';
import SiteChatWidget from '@/components/SiteChatWidget';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AnalyticsProvider } from '@/contexts/Analytics';
import { setupChunkLoadErrorRecovery } from '@/utils/chunkLoadRecovery';
import { captureMarketingData } from '@/utils/marketingCapture';
import { MyUserContextProvider } from '@/utils/useUser';
// Create a client
const queryClient = new QueryClient();
// Stable container resolver for all antd portals (Tooltip, Dropdown, Modal,
// notification, message, etc). MUST be defined at module scope — an inline
// `() => document.body` would create a new function on every render and
// defeat the bail-out in @rc-component/portal's setInnerContainer effect,
// reintroducing the React #185 "Maximum update depth" crash on Win/Edge.
// Repro: open /chat in Edge on Windows and start streaming a long response
// while a Tooltip-wrapped button is mounted. See https://react.dev/errors/185
const getBodyContainer = () => document.body;
function MyApp(props: AppProps) {
    const router = useRouter();
    // Mobile login opens this page in a fresh WebView. Mount only the security
    // widget: hiding the consent banner alone still allowed GTM, PostHog and
    // marketing capture to run. Keep the website's effects/providers in a
    // separate component so this branch never initializes them (including SSR).
    if (router.pathname === '/turnstile') {
        const { Component, pageProps } = props;
        return <Component {...pageProps}/>;
    }
    return <WebsiteApp {...props}/>;
}
function WebsiteApp({ Component, pageProps }: AppProps) {
    useEffect(() => {
        captureMarketingData();
        const teardownChunkRecovery = setupChunkLoadErrorRecovery();
        const handleRouteChangeStart = () => NProgress.start();
        const handleRouteChangeDone = () => NProgress.done();
        Router.events.on('routeChangeStart', handleRouteChangeStart);
        Router.events.on('routeChangeComplete', handleRouteChangeDone);
        Router.events.on('routeChangeError', handleRouteChangeDone);
        return () => {
            teardownChunkRecovery();
            Router.events.off('routeChangeStart', handleRouteChangeStart);
            Router.events.off('routeChangeComplete', handleRouteChangeDone);
            Router.events.off('routeChangeError', handleRouteChangeDone);
        };
    }, []);
    // Deferred Google Tag Manager script - loads after page becomes interactive
    const gtmScript = (<Script id='gtm' strategy='afterInteractive' dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${GTM_ID}');`,
        }}/>);
    if (pageProps?.isStaticPage) {
        return (<AppErrorBoundary>
        <ConfigProvider getPopupContainer={getBodyContainer}>
          <TooltipProvider delayDuration={200}>
            {gtmScript}
            <AnalyticsProvider>
              <LocaleInitializer />
              <Component {...pageProps}/>
            </AnalyticsProvider>
            <SiteChatWidget />
          </TooltipProvider>
        </ConfigProvider>
      </AppErrorBoundary>);
    }
    return (<AppErrorBoundary>
      <ConfigProvider getPopupContainer={getBodyContainer}>
        <TooltipProvider delayDuration={200}>
          <MyUserContextProvider>
            <QueryClientProvider client={queryClient}>
              {/* Google Tag Manager - loads after page becomes interactive */}
              {gtmScript}

              <AnalyticsProvider>
                <LocaleInitializer />
                <Component {...pageProps}/>
              </AnalyticsProvider>
              <Toaster position='bottom-center' duration={5000} closeButton richColors theme='system'/>
            </QueryClientProvider>
            <CookieConsent />
            <SiteChatWidget />
          </MyUserContextProvider>
        </TooltipProvider>
      </ConfigProvider>
    </AppErrorBoundary>);
}
const __Next_Translate__Page__1a0cbfb655f__ = MyApp;

    export default __appWithI18n(__Next_Translate__Page__1a0cbfb655f__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: true,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../locales/' + l + '/' + n).then(m => m.default)),
    });
  